<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, inject } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import conversationService from '../services/conversation.service'
import personService from '../services/person.service'

import ConversationView from './ConversationView.vue'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const authStore = useAuthStore()
const isKeyboardOpen = inject('isKeyboardOpen', ref(false))

// Active conversation UUID from route
const activeConversationUuid = computed(() => route.params.uuid as string | undefined)

// Read safe area inset value in pixels using CSS variable (respects platform overrides)
const getSafeAreaInset = (position: 'top' | 'bottom'): number => {
  const div = document.createElement('div')
  div.style.position = 'fixed'
  div.style.left = '0'
  div.style.width = '0'
  div.style.height = `var(--safe-area-${position})`
  document.body.appendChild(div)
  const value = div.offsetHeight
  document.body.removeChild(div)
  return value
}

// Dynamic height for conversations list (mobile)
const conversationsListHeight = ref<string>('auto')
const calculateConversationsHeight = () => {
  const availableHeight = window.innerHeight
  const safeTop = getSafeAreaInset('top')
  const safeBottom = getSafeAreaInset('bottom')
  const h = availableHeight - 206 - safeTop - safeBottom
  conversationsListHeight.value = h + 'px'
}

// Conversations state
const conversations = ref<any[]>([])
const isLoading = ref(false)
const totalConversations = ref(0)
const currentOffset = ref(0)
const conversationsPerPage = 15
const hasMoreConversations = ref(true)

// Scroll container ref
const conversationsListRef = ref<HTMLElement | null>(null)

// Available users for new conversation (fetched from API)
const availableUsers = ref<Array<{
  uuid: string
  first_name: string
  last_name: string
  role: string
  full_name?: string
}>>([])
const isLoadingUsers = ref(false)

// New conversation modal state
const showNewConversationModal = ref(false)
const searchQuery = ref('')
const selectedUsers = ref<string[]>([]) // Changed to string[] for UUIDs
const groupName = ref('')
const groupNameError = ref(false)
const validationError = ref('')

// Filtered users based on search
const filteredUsers = computed(() => {
  if (!searchQuery.value) return availableUsers.value

  const query = searchQuery.value.toLowerCase()
  return availableUsers.value.filter(user => {
    const fullName = `${user.first_name} ${user.last_name}`.toLowerCase()
    return fullName.includes(query) || user.role.toLowerCase().includes(query)
  })
})

// Check if user is selected
const isUserSelected = (userUuid: string) => {
  return selectedUsers.value.includes(userUuid)
}

// Toggle user selection
const toggleUserSelection = (userUuid: string) => {
  const index = selectedUsers.value.indexOf(userUuid)
  if (index > -1) {
    selectedUsers.value.splice(index, 1)
  } else {
    selectedUsers.value.push(userUuid)
  }
}

// Fetch available users from API
const fetchAvailableUsers = async () => {
  try {
    isLoadingUsers.value = true
    const clubUuid = authStore.userInitData?.club?.uuid

    if (!clubUuid) {
      console.error('Club UUID not found')
      return
    }

    const response = await personService.getPersons(clubUuid)

    // Map role names to translations
    const roleTranslations: { [key: string]: string } = {
      'OWNER': t('conversations.roleOwner'),
      'SUPERVISOR': t('conversations.roleSupervisor'),
      'PLAYER': t('conversations.rolePlayer'),
      'COACH': t('conversations.roleCoach')
    }

    availableUsers.value = response.persons.map((person: any) => ({
      ...person,
      full_name: `${person.first_name} ${person.last_name}`,
      role: roleTranslations[person.role] || person.role
    }))
  } catch (error) {
    console.error('Error fetching users:', error)
  } finally {
    isLoadingUsers.value = false
  }
}

// Open new conversation modal
const openNewConversationModal = async () => {
  showNewConversationModal.value = true
  searchQuery.value = ''
  selectedUsers.value = []
  groupName.value = ''
  groupNameError.value = false
  validationError.value = ''

  // Fetch users when opening modal
  await fetchAvailableUsers()
}

// Close new conversation modal
const closeNewConversationModal = () => {
  showNewConversationModal.value = false
  searchQuery.value = ''
  selectedUsers.value = []
  groupName.value = ''
  groupNameError.value = false
  validationError.value = ''
}

// Create new conversation
const createConversation = async () => {
  // Reset errors
  groupNameError.value = false
  validationError.value = ''

  if (selectedUsers.value.length === 0) {
    validationError.value = t('conversations.selectAtLeastOneUser')
    return
  }

  if (selectedUsers.value.length > 1 && !groupName.value.trim()) {
    groupNameError.value = true
    validationError.value = t('conversations.groupNameRequired')
    return
  }

  try {
    // Get club UUID from auth store
    const clubUuid = authStore.userInitData?.club?.uuid
    if (!clubUuid) {
      validationError.value = t('conversations.clubUuidNotFound')
      return
    }

    // Get UUIDs of selected users (already are UUIDs)
    const participantUuids = selectedUsers.value

    // Prepare request data
    const isGroup = selectedUsers.value.length > 1
    const requestData: any = {
      participant_uuids: participantUuids,
      is_group: isGroup
    }

    // Add group name if it's a group conversation
    if (isGroup && groupName.value.trim()) {
      requestData.name = groupName.value.trim()
    }

    // Call API to create conversation
    const response = await conversationService.createConversation(clubUuid, requestData)

    // Close modal
    closeNewConversationModal()

    // Add new conversation to the beginning of the list only if it's a group
    if (response.conversation && isGroup) {
      conversations.value.unshift(response.conversation)
      totalConversations.value += 1
    }

    // Open conversation (existing or newly created)
    if (response.conversation?.uuid) {
      openConversation(response.conversation.uuid)
    }
  } catch (error: any) {
    console.error('Error creating conversation:', error)

    // Handle validation errors from backend
    if (error.response?.data?.errors) {
      // Get first validation error message
      const errors = error.response.data.errors
      const firstErrorKey = Object.keys(errors)[0]
      validationError.value = errors[firstErrorKey][0] || t('conversations.validationError')
    } else {
      validationError.value = error.response?.data?.message || t('conversations.errorCreating')
    }
  }
}

// Clear group name error when user starts typing
const handleGroupNameInput = () => {
  if (groupNameError.value) {
    groupNameError.value = false
  }
}

// Open conversation (update route without reload)
const activeConversation = computed(() =>
  conversations.value.find(c => c.uuid === activeConversationUuid.value)
)

const openConversation = (conversationUuid: string) => {
  router.push(`/conversations/${conversationUuid}`)
}

// Close conversation (go back to conversations list)
const closeConversation = () => {
  router.push('/conversations')
}

// Get avatar color class
const getAvatarClass = (isGroup: boolean) => {
  return isGroup ? 'bg-green-500' : 'bg-blue-500'
}

// Get avatar content
const getAvatarContent = (name: string, isGroup: boolean) => {
  return isGroup ? '👥' : name.charAt(0).toUpperCase()
}

// Format time helper
const formatTime = (dateString: string | null) => {
  if (!dateString) return ''

  const messageDate = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - messageDate.getTime()
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))

  const hours = String(messageDate.getHours()).padStart(2, '0')
  const minutes = String(messageDate.getMinutes()).padStart(2, '0')

  // Less than 1 minute
  if (diffMinutes < 1) return t('conversations.timeNow')

  // Less than 60 minutes
  if (diffMinutes < 60) return t('conversations.timeMinAgo', { minutes: diffMinutes })

  // Between 1 and 2 hours
  if (diffHours >= 1 && diffHours < 2) return t('conversations.timeHourAgo')

  // Check if today
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const messageDateStart = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate())
  const daysDiff = Math.floor((todayStart.getTime() - messageDateStart.getTime()) / (1000 * 60 * 60 * 24))

  // Today
  if (daysDiff === 0) return `${hours}:${minutes}`

  // Yesterday
  if (daysDiff === 1) return t('conversations.timeYesterday', { time: `${hours}:${minutes}` })

  // Older
  const day = String(messageDate.getDate()).padStart(2, '0')
  const month = String(messageDate.getMonth() + 1).padStart(2, '0')
  const year = messageDate.getFullYear()

  return `${year}-${month}-${day} ${hours}:${minutes}`
}

// Format last message helper
const formatLastMessage = (conversation: any) => {
  if (!conversation.last_message) return ''

  const message = conversation.last_message
  let text = message.content || ''

  // If group, prepend sender name
  if (conversation.is_group && message.sender) {
    text = `${message.sender.first_name} ${message.sender.last_name}: ${text}`
  }

  return text
}

// Load conversations
const loadConversations = async (append = false) => {
  if (isLoading.value || (!append && conversations.value.length > 0)) return

  try {
    isLoading.value = true
    const clubUuid = authStore.userInitData?.club?.uuid

    if (!clubUuid) {
      console.error('Club UUID not found')
      return
    }

    const response = await conversationService.getConversations(clubUuid, {
      limit: conversationsPerPage,
      offset: currentOffset.value
    })

    if (append) {
      conversations.value.push(...response.conversations)
    } else {
      conversations.value = response.conversations
    }

    totalConversations.value = response.total
    currentOffset.value += response.conversations.length

    // Check if there are more conversations to load
    hasMoreConversations.value = conversations.value.length < totalConversations.value
  } catch (error) {
    console.error('Error loading conversations:', error)
  } finally {
    isLoading.value = false
  }
}

// Load more conversations
const loadMoreConversations = () => {
  if (!hasMoreConversations.value || isLoading.value) return
  loadConversations(true)
}

// Handle scroll event
const handleScroll = (event: Event) => {
  const target = event.target as HTMLElement
  const scrollTop = target.scrollTop
  const scrollHeight = target.scrollHeight
  const clientHeight = target.clientHeight

  // Check if scrolled to bottom (with 100px threshold)
  if (scrollTop + clientHeight >= scrollHeight - 100) {
    loadMoreConversations()
  }
}

// Reload conversations list (e.g., after creating new conversation)
const reloadConversations = async () => {
  conversations.value = []
  currentOffset.value = 0
  hasMoreConversations.value = true
  await loadConversations()
}

// Handle conversation marked as read
const handleConversationRead = (conversationUuid: string) => {
  const conversation = conversations.value.find(conv => conv.uuid === conversationUuid)
  if (conversation) {
    conversation.is_unread = false
  }
}

// Handle message sent - update conversation list
const handleMessageSent = async (data: { conversationUuid: string, message: any }) => {
  const { conversationUuid, message } = data
  const currentPerson = authStore.userInitData?.persons?.current

  // Find conversation in the list
  const existingConversation = conversations.value.find(conv => conv.uuid === conversationUuid)

  if (existingConversation) {
    // Conversation exists - use newMessageInConversation
    newMessageInConversation({
      conversationUuid,
      conversationName: existingConversation.name || t('conversations.unnamed'),
      lastMessage: message.content,
      lastMessageAt: message.created_at,
      isGroup: existingConversation.is_group,
      isUnread: false, // My message, so it's read
      senderUuid: currentPerson?.uuid || '',
      senderName: `${message.sender.first_name} ${message.sender.last_name}`
    })
  } else {
    // Conversation doesn't exist (new 1-on-1) - fetch it and add to list
    try {
      const clubUuid = authStore.userInitData?.club?.uuid
      if (!clubUuid) return

      const response = await conversationService.getConversation(clubUuid, conversationUuid)
      if (response.conversation) {
        newMessageInConversation({
          conversationUuid,
          conversationName: response.conversation.name || t('conversations.unnamed'),
          lastMessage: message.content,
          lastMessageAt: message.created_at,
          isGroup: response.conversation.is_group,
          isUnread: false, // My message, so it's read
          senderUuid: currentPerson?.uuid || '',
          senderName: `${message.sender.first_name} ${message.sender.last_name}`
        })
      }
    } catch (error) {
      console.error('Error fetching conversation:', error)
    }
  }
}

// New message in conversation - updates conversation list with new message
const newMessageInConversation = (params: {
  conversationUuid: string
  conversationName: string
  lastMessage: string
  lastMessageAt: string
  isGroup: boolean
  isUnread: boolean
  senderUuid: string
  senderName: string
}) => {
  const {
    conversationUuid,
    conversationName,
    lastMessage,
    lastMessageAt,
    isGroup,
    isUnread,
    senderUuid,
    senderName
  } = params

  // Remove conversation from current position if it exists
  const existingIndex = conversations.value.findIndex(conv => conv.uuid === conversationUuid)
  let conversation: any

  if (existingIndex !== -1) {
    // Remove from current position
    conversation = conversations.value.splice(existingIndex, 1)[0]

    // Update conversation data
    conversation.name = conversationName
    conversation.last_message = {
      content: lastMessage,
      sender: {
        uuid: senderUuid,
        first_name: senderName.split(' ')[0] || '',
        last_name: senderName.split(' ').slice(1).join(' ') || ''
      }
    }
    conversation.last_message_at = lastMessageAt
    conversation.is_group = isGroup
    conversation.is_unread = isUnread
  } else {
    // Create new conversation object
    conversation = {
      uuid: conversationUuid,
      name: conversationName,
      last_message: {
        content: lastMessage,
        sender: {
          uuid: senderUuid,
          first_name: senderName.split(' ')[0] || '',
          last_name: senderName.split(' ').slice(1).join(' ') || ''
        }
      },
      last_message_at: lastMessageAt,
      is_group: isGroup,
      is_unread: isUnread
    }
    totalConversations.value += 1
  }

  // Find correct position based on last_message_at (sorted by newest first)
  const newMessageTime = new Date(lastMessageAt).getTime()
  let insertIndex = 0

  for (let i = 0; i < conversations.value.length; i++) {
    const convTime = new Date(conversations.value[i].last_message_at || 0).getTime()
    if (newMessageTime > convTime) {
      insertIndex = i
      break
    }
    insertIndex = i + 1
  }

  // Insert conversation at correct position
  conversations.value.splice(insertIndex, 0, conversation)
}

// Handle message.sent forwarded from AppNavigation via custom event
const handleEchoMessageSent = (event: CustomEvent) => {
  const data = event.detail
  if (!data) return

  newMessageInConversation({
    conversationUuid: data.conversationUuid,
    conversationName: data.conversationName,
    lastMessage: data.lastMessage,
    lastMessageAt: data.lastMessageAt,
    isGroup: data.isGroup,
    isUnread: data.isUnread,
    senderUuid: data.senderUuid,
    senderName: data.senderName
  })
}

// Lifecycle
onMounted(() => {
  document.body.classList.add('no-fixed-body')
  calculateConversationsHeight()
  window.addEventListener('resize', calculateConversationsHeight)
  loadConversations()
  window.addEventListener('echoMessageSent', handleEchoMessageSent as EventListener)
})

onBeforeUnmount(() => {
  document.body.classList.remove('no-fixed-body')
  window.removeEventListener('resize', calculateConversationsHeight)
  window.removeEventListener('echoMessageSent', handleEchoMessageSent as EventListener)
})
</script>

<template>
  <!-- Mobile layout (below 768px) -->
  <!-- Mobile: Show conversation if selected -->
  <div v-if="activeConversationUuid" class="md:hidden">
    <!-- WhatsApp redirect panel for category conversations with WhatsApp -->
    <div v-if="activeConversation?.whatsapp_enabled && activeConversation?.whatsapp_link" class="flex flex-col h-full bg-gray-50">
      <div class="flex items-center gap-3 px-4 py-3 border-b-2 border-gray-300 bg-white">
        <button @click="closeConversation" class="text-gray-500 hover:text-gray-700">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
        </button>
        <span class="font-semibold text-gray-900">{{ activeConversation.name }}</span>
      </div>
      <div class="flex flex-col items-center justify-center flex-1 p-8 text-center">
        <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.118 1.525 5.847L0 24l6.334-1.508A11.956 11.956 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.88 0-3.636-.488-5.163-1.342l-.371-.22-3.762.896.944-3.653-.242-.382A9.956 9.956 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
        </div>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ t('conversations.whatsappGroupTitle') }}</h3>
        <p class="text-sm text-gray-500 mb-6">{{ t('conversations.whatsappGroupDesc') }}</p>
        <a
          :href="activeConversation.whatsapp_link"
          target="_blank"
          rel="noopener noreferrer"
          class="px-6 py-3 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700"
        >{{ t('conversations.whatsappGroupOpen') }}</a>
      </div>
    </div>
    <ConversationView
      v-else
      :conversation-uuid="activeConversationUuid"
      :show-sidebar="false"
      @close="closeConversation"
      @conversation-read="handleConversationRead"
      @message-sent="handleMessageSent"
    />
  </div>

  <!-- Mobile: Show conversations list if no conversation selected -->
  <div v-else class="md:hidden bg-gray-50">
    <!-- Header bar: title + new conversation button -->
    <div class="flex items-center justify-between px-4 py-4 border-b-2 border-gray-300 bg-white">
      <h2 class="text-xl font-bold text-gray-900">{{ t('conversations.title') }}</h2>
      <button
        @click="openNewConversationModal"
        class="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
        :title="t('conversations.newConversation')"
      >
        <span class="text-white text-2xl leading-none">+</span>
      </button>
    </div>

    <!-- Conversations List Container: 100vh - 66px nav - 66px header - 70px bottom nav -->
    <div ref="conversationsListRef" class="overflow-y-auto bg-white" :style="{ height: conversationsListHeight }" @scroll="handleScroll">
      <!-- Empty state when no conversations -->
      <div v-if="!isLoading && conversations.length === 0" class="text-center py-12 px-4">
        <div class="w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
          <svg class="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clip-rule="evenodd"/>
          </svg>
        </div>
        <p class="text-gray-500">{{ t('conversations.noConversations') }}</p>
        <p class="text-sm text-gray-400 mt-1">{{ t('conversations.startNewConversation') }}</p>
      </div>

      <!-- Conversations list -->
      <div
        v-for="conv in conversations"
        :key="'mobile-' + conv.uuid"
        @click="openConversation(conv.uuid)"
        :class="[
          'block px-4 py-3 border-b border-gray-200 hover:bg-gray-100 cursor-pointer transition-colors',
          conv.is_unread ? 'bg-blue-50' : ''
        ]"
      >
        <div class="flex items-start gap-3">
          <div :class="['w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center text-white font-semibold', getAvatarClass(conv.is_group)]">
            {{ getAvatarContent(conv.name || 'U', conv.is_group) }}
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between mb-1">
              <h3 :class="['text-sm text-gray-900 truncate', conv.is_unread ? 'font-bold' : 'font-semibold']">
                {{ conv.name || t('conversations.unnamed') }}
              </h3>
              <span class="text-xs text-gray-500 ml-2 flex-shrink-0">{{ formatTime(conv.last_message_at) }}</span>
            </div>
            <p :class="['text-sm text-gray-600 truncate', conv.is_unread ? 'font-semibold' : '']">
              {{ formatLastMessage(conv) }}
            </p>
          </div>
          <div v-if="conv.is_unread" class="w-3 h-3 bg-blue-600 rounded-full flex-shrink-0 mt-1"></div>
        </div>
      </div>

      <!-- Loading Indicator -->
      <div v-if="isLoading" class="flex items-center justify-center py-6">
        <div class="flex items-center gap-2 text-gray-600">
          <svg class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span class="text-sm">{{ t('conversations.loading') }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Desktop layout (768px+) -->
  <div class="hidden md:block bg-gray-50" style="height: calc(100vh - 64px); overflow: hidden;">
    <div class="h-full flex">
      <!-- Left Sidebar - Conversations List -->
      <div :class="[
        'bg-white border-r-2 border-gray-300 flex flex-col',
        activeConversationUuid ? 'hidden lg:flex lg:w-1/4 lg:min-w-[300px]' : 'w-full lg:w-1/4 lg:min-w-[300px]'
      ]">
        <!-- Header -->
        <div class="sticky top-0 z-10 flex items-center justify-between px-4 py-4 border-b-2 border-gray-300 bg-white">
          <h2 class="text-xl font-bold text-gray-900">{{ t('conversations.title') }}</h2>
          <button
            @click="openNewConversationModal"
            class="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
            :title="t('conversations.newConversation')"
          >
            <span class="text-white text-2xl leading-none">+</span>
          </button>
        </div>

        <!-- Conversations List -->
        <div ref="conversationsListRef" class="flex-1 overflow-y-auto" @scroll="handleScroll">
          <!-- Empty state when no conversations -->
          <div v-if="!isLoading && conversations.length === 0" class="text-center py-12 px-4">
            <div class="w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
              <svg class="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clip-rule="evenodd"/>
              </svg>
            </div>
            <p class="text-gray-500">{{ t('conversations.noConversations') }}</p>
            <p class="text-sm text-gray-400 mt-1">{{ t('conversations.startNewConversation') }}</p>
          </div>

          <!-- Conversations list -->
          <div
            v-for="conv in conversations"
            :key="conv.uuid"
            @click="openConversation(conv.uuid)"
            :class="[
              'block px-4 py-3 border-b border-gray-200 hover:bg-gray-100 cursor-pointer transition-colors',
              conv.is_unread ? 'bg-blue-50' : ''
            ]"
          >
            <div class="flex items-start gap-3">
              <!-- Avatar -->
              <div :class="[
                'w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center text-white font-semibold',
                getAvatarClass(conv.is_group)
              ]">
                {{ getAvatarContent(conv.name || 'U', conv.is_group) }}
              </div>

              <!-- Content -->
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between mb-1">
                  <h3 :class="[
                    'text-sm text-gray-900 truncate',
                    conv.is_unread ? 'font-bold' : 'font-semibold'
                  ]">
                    {{ conv.name || t('conversations.unnamed') }}
                  </h3>
                  <span class="text-xs text-gray-500 ml-2 flex-shrink-0">
                    {{ formatTime(conv.last_message_at) }}
                  </span>
                </div>
                <p :class="[
                  'text-sm text-gray-600 truncate',
                  conv.is_unread ? 'font-semibold' : ''
                ]">
                  {{ formatLastMessage(conv) }}
                </p>
              </div>

              <!-- Unread indicator -->
              <div v-if="conv.is_unread" class="w-3 h-3 bg-blue-600 rounded-full flex-shrink-0 mt-1"></div>
            </div>
          </div>

          <!-- Loading Indicator -->
          <div v-if="isLoading" class="flex items-center justify-center py-6">
            <div class="flex items-center gap-2 text-gray-600">
              <svg class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span class="text-sm">{{ t('conversations.loading') }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Side - Conversation Area (3/4 width on desktop above 1024px, full width on mobile when conversation selected) -->
      <div :class="[
        'flex flex-col bg-gray-50',
        activeConversationUuid ? 'flex-1 w-full lg:w-3/4' : 'hidden lg:flex lg:flex-1 lg:w-3/4'
      ]">
        <!-- WhatsApp redirect panel for category conversations with WhatsApp -->
        <div v-if="activeConversationUuid && activeConversation?.whatsapp_enabled && activeConversation?.whatsapp_link" class="flex flex-col flex-1">
          <div class="flex items-center gap-3 px-4 py-3 border-b-2 border-gray-300 bg-white flex-shrink-0">
            <div class="w-9 h-9 rounded-full bg-green-500 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
              {{ activeConversation.name?.charAt(0).toUpperCase() }}
            </div>
            <span class="font-semibold text-gray-900">{{ activeConversation.name }}</span>
          </div>
          <div class="flex flex-col items-center justify-center flex-1 p-8 text-center">
            <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <svg class="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.118 1.525 5.847L0 24l6.334-1.508A11.956 11.956 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.88 0-3.636-.488-5.163-1.342l-.371-.22-3.762.896.944-3.653-.242-.382A9.956 9.956 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
            </div>
            <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ t('conversations.whatsappGroupTitle') }}</h3>
            <p class="text-sm text-gray-500 mb-6">{{ t('conversations.whatsappGroupDesc') }}</p>
            <a
              :href="activeConversation.whatsapp_link"
              target="_blank"
              rel="noopener noreferrer"
              class="px-6 py-3 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700"
            >{{ t('conversations.whatsappGroupOpen') }}</a>
          </div>
        </div>

        <!-- Show conversation if UUID is in route -->
        <ConversationView
          v-else-if="activeConversationUuid"
          :conversation-uuid="activeConversationUuid"
          :show-sidebar="false"
          @close="closeConversation"
          @conversation-read="handleConversationRead"
          @message-sent="handleMessageSent"
        />

        <!-- Empty State when no conversation selected (hidden below 1024px) -->
        <div v-else class="hidden lg:flex flex-col items-center justify-center h-full">
          <div class="text-center">
            <div class="w-24 h-24 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
              <svg class="w-12 h-12 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clip-rule="evenodd"/>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-700 mb-2">{{ t('conversations.selectConversation') }}</h3>
            <p class="text-gray-500">{{ t('conversations.selectConversationHint') }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- New Conversation Modal -->
  <div
    v-if="showNewConversationModal"
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    @click.self="closeNewConversationModal"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] flex flex-col">
      <!-- Modal Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b-2 border-gray-300">
        <h2 class="text-xl font-bold text-gray-900">{{ t('conversations.newConversationTitle') }}</h2>
        <button
          @click="closeNewConversationModal"
          class="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
        >
          <svg class="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
        </button>
      </div>

      <!-- Search Input -->
      <div class="px-6 py-4 border-b border-gray-200">
        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t('conversations.searchPerson')"
            class="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600"
          />
          <svg class="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"/>
          </svg>
        </div>
      </div>

      <!-- Users List -->
      <div class="flex-1 overflow-y-auto px-6 py-4">
        <!-- Loading state -->
        <div v-if="isLoadingUsers" class="text-center py-8">
          <div class="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-2"></div>
          <p class="text-sm text-gray-500">{{ t('conversations.loadingUsers') }}</p>
        </div>

        <!-- Users list -->
        <div v-else>
          <div
            v-for="user in filteredUsers"
            :key="user.uuid"
            @click="toggleUserSelection(user.uuid)"
            class="flex items-center gap-3 p-3 border-2 border-gray-200 rounded-lg mb-3 cursor-pointer hover:bg-gray-50 transition-colors"
            :class="isUserSelected(user.uuid) ? 'bg-blue-50 border-blue-600' : ''"
          >
            <input
              type="checkbox"
              :checked="isUserSelected(user.uuid)"
              class="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-600"
              @click.stop="toggleUserSelection(user.uuid)"
            />
            <div class="w-10 h-10 rounded-full bg-blue-500 flex-shrink-0 flex items-center justify-center text-white font-semibold">
              {{ user.first_name.charAt(0).toUpperCase() }}
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-gray-900 truncate">{{ user.full_name }}</p>
              <p class="text-xs text-gray-500 truncate">{{ user.role }}</p>
            </div>
          </div>

          <!-- Empty state for search -->
          <div v-if="filteredUsers.length === 0" class="text-center py-8">
            <p class="text-gray-500">{{ t('conversations.noUsersFound') }}</p>
          </div>
        </div>
      </div>

      <!-- Group Name Input (visible when > 1 user selected) -->
      <div v-if="selectedUsers.length > 1" class="px-6 py-4 border-t border-gray-200">
        <label class="block text-sm font-semibold text-gray-900 mb-2">{{ t('conversations.groupName') }}</label>
        <input
          v-model="groupName"
          type="text"
          :placeholder="t('conversations.groupNamePlaceholder')"
          @input="handleGroupNameInput"
          :class="[
            'w-full px-4 py-2 border-2 rounded-lg focus:outline-none',
            groupNameError ? 'border-red-600 focus:border-red-600' : 'border-gray-300 focus:border-blue-600'
          ]"
        />
        <p v-if="groupNameError" class="text-xs text-red-600 mt-1">{{ t('conversations.groupNameRequired') }}</p>
      </div>

      <!-- Modal Footer -->
      <div class="px-6 py-4 border-t-2 border-gray-300">
        <!-- Validation Error Message -->
        <div v-if="validationError" class="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p class="text-sm text-red-600">{{ validationError }}</p>
        </div>

        <div class="flex items-center justify-between">
          <p class="text-sm text-gray-600">{{ t('conversations.selected') }} {{ selectedUsers.length }}</p>
          <div class="flex gap-2">
            <button
              @click="closeNewConversationModal"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
            >
              {{ t('common.cancel') }}
            </button>
            <button
              @click="createConversation"
              :disabled="selectedUsers.length === 0"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {{ selectedUsers.length > 1 ? t('conversations.createGroup') : t('conversations.startConversation') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
